import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type LookupItem,
  type SaleContract,
  type SaleContractPayload,
  useCreateSaleContractMutation,
  useCopyDraftMutation,
  useGetSaleContractQuery,
  useSuggestContractNoQuery,
  useUpdateSaleContractMutation,
  useGetLookupContractTypesQuery,
  useGetLookupFixationCallsQuery,
  useGetLookupPriceUnitsQuery,
  useGetLookupFuturesMonthsQuery,
  useGetLookupDeliveryTermsQuery,
  useGetLookupPaymentWeightTermsQuery,
  useGetLookupConditionsQuery,
  useGetLookupCertificationsQuery,
  useLazyGetLookupClientsQuery,
  useLazyGetLookupConsigneesQuery,
  useLazyGetLookupPackagesQuery,
  useLazyGetLookupQualitiesQuery,
  useLazyGetLookupBrokersQuery,
  useLazyGetLookupTradersQuery,
} from '../../store/api/saleContractApi'

/* ------------------------------------------------------------------ */
/*  Form model                                                         */
/* ------------------------------------------------------------------ */

interface SaleContractFormModel {
  contractNo: string
  buyerRef: string
  contractDate: string
  clientCode: string
  clientName: string
  consigneeCode: string
  consigneeName: string
  quantity: number | ''
  packagingCode: string
  packagingName: string
  bags: number | ''
  qualityCode: string
  certificationCode: string
  contractType: string
  fixationCall: string
  price: number | ''
  priceUnit: string
  ptbfDiff: number | ''
  vatApplicable: boolean | null
  vatRate: number | ''
  futuresMonth: string
  fxRate: number | ''
  deliveryTerm: string
  warehouse: string
  shipmentDateFrom: string
  shipmentDateTo: string
  deliveryDateFrom: string
  deliveryDateTo: string
  isShipDeli: number
  pss: string
  paymentWeightTerm: string
  franchise: number | ''
  paymentTerms: string
  conditionsCode: string
  note: string
  freight: number | ''
  logisticsCost: number | ''
  insuranceCost: number | ''
  weightingCost: number | ''
  financeCost: number | ''
  bankCost: number | ''
  description: string
  isIncludedRoyal: boolean
  royalFee: number | ''
  brokerCode: string
  brokerFirm: string
  brokerRef: string
  commission: number | ''
  traderCode: string
  traderName: string
  sentContract: boolean
  sentPaper: boolean
  receivedContract: boolean
  receivedPaper: boolean
}

const emptyForm = (): SaleContractFormModel => ({
  contractNo: '',
  buyerRef: '',
  contractDate: new Date().toISOString().slice(0, 10),
  clientCode: '',
  clientName: '',
  consigneeCode: '',
  consigneeName: '',
  quantity: '',
  packagingCode: '',
  packagingName: '',
  bags: '',
  qualityCode: '',
  certificationCode: '',
  contractType: '',
  fixationCall: '',
  price: '',
  priceUnit: '',
  ptbfDiff: '',
  vatApplicable: false,
  vatRate: '',
  futuresMonth: '',
  fxRate: '',
  deliveryTerm: '',
  warehouse: '',
  shipmentDateFrom: '',
  shipmentDateTo: '',
  deliveryDateFrom: '',
  deliveryDateTo: '',
  isShipDeli: 1,
  pss: '',
  paymentWeightTerm: '',
  franchise: '',
  paymentTerms: '',
  conditionsCode: '',
  note: '',
  freight: '',
  logisticsCost: '',
  insuranceCost: '',
  weightingCost: '',
  financeCost: '',
  bankCost: '',
  description: '',
  isIncludedRoyal: false,
  royalFee: '',
  brokerCode: '',
  brokerFirm: '',
  brokerRef: '',
  commission: '',
  traderCode: '',
  traderName: '',
  sentContract: false,
  sentPaper: false,
  receivedContract: false,
  receivedPaper: false,
})

const toNum = (v: number | ''): number | null => (v === '' ? null : v)

const contractToForm = (c: SaleContract | SaleContractPayload): SaleContractFormModel => ({
  contractNo: c.contractNo,
  buyerRef: c.buyerRef ?? '',
  contractDate: c.contractDate,
  clientCode: c.clientCode,
  clientName: ('clientName' in c ? c.clientName : null) ?? '',
  consigneeCode: c.consigneeCode ?? '',
  consigneeName: ('consigneeName' in c ? c.consigneeName : null) ?? '',
  quantity: c.quantity ?? '',
  packagingCode: c.packagingCode ?? '',
  packagingName: ('packaging' in c ? c.packaging : null) ?? '',
  bags: c.bags ?? '',
  qualityCode: c.qualityCode ?? '',
  certificationCode: c.certificationCode ?? '',
  contractType: c.contractType,
  fixationCall: c.fixationCall ?? '',
  price: c.price ?? '',
  priceUnit: c.priceUnit ?? '',
  ptbfDiff: c.ptbfDiff ?? '',
  vatApplicable: c.vatApplicable ?? null,
  vatRate: c.vatRate ?? '',
  futuresMonth: c.futuresMonth ?? '',
  fxRate: c.fxRate ?? '',
  deliveryTerm: c.deliveryTerm ?? '',
  warehouse: c.warehouse ?? '',
  shipmentDateFrom: c.shipmentDateFrom ?? '',
  shipmentDateTo: c.shipmentDateTo ?? '',
  deliveryDateFrom: c.deliveryDateFrom ?? '',
  deliveryDateTo: c.deliveryDateTo ?? '',
  isShipDeli: c.isShipDeli ?? 0,
  pss: c.pss ?? '',
  paymentWeightTerm: c.paymentWeightTerm ?? '',
  franchise: c.franchise ?? '',
  paymentTerms: c.paymentTerms ?? '',
  conditionsCode: c.conditionsCode ?? '',
  note: c.note ?? '',
  freight: c.freight ?? '',
  logisticsCost: c.logisticsCost ?? '',
  insuranceCost: c.insuranceCost ?? '',
  weightingCost: c.weightingCost ?? '',
  financeCost: c.financeCost ?? '',
  bankCost: c.bankCost ?? '',
  description: c.description ?? '',
  isIncludedRoyal: c.isIncludedRoyal ?? false,
  royalFee: c.royalFee ?? '',
  brokerCode: c.brokerCode ?? '',
  brokerFirm: ('brokerFirm' in c ? c.brokerFirm : null) ?? '',
  brokerRef: c.brokerRef ?? '',
  commission: c.commission ?? '',
  traderCode: c.traderCode ?? '',
  traderName: ('traderName' in c ? c.traderName : null) ?? '',
  sentContract: c.sentContract,
  sentPaper: c.sentPaper,
  receivedContract: c.receivedContract,
  receivedPaper: c.receivedPaper,
})

const formToPayload = (f: SaleContractFormModel): SaleContractPayload => ({
  contractNo: f.contractNo,
  buyerRef: f.buyerRef || null,
  contractDate: f.contractDate,
  clientCode: f.clientCode,
  consigneeCode: f.consigneeCode || null,
  quantity: toNum(f.quantity) ?? 0,
  packagingCode: f.packagingCode || null,
  bags: toNum(f.bags),
  qualityCode: f.qualityCode || null,
  certificationCode: f.certificationCode || null,
  contractType: f.contractType,
  fixationCall: f.fixationCall || null,
  price: toNum(f.price),
  priceUnit: f.priceUnit || null,
  ptbfDiff: toNum(f.ptbfDiff),
  vatApplicable: f.vatApplicable,
  vatRate: toNum(f.vatRate),
  futuresMonth: f.futuresMonth || null,
  fxRate: toNum(f.fxRate),
  deliveryTerm: f.deliveryTerm || null,
  warehouse: f.warehouse.trim() ? f.warehouse : '',
  shipmentDateFrom: f.shipmentDateFrom || null,
  shipmentDateTo: f.shipmentDateTo || null,
  deliveryDateFrom: f.deliveryDateFrom || null,
  deliveryDateTo: f.deliveryDateTo || null,
  isShipDeli: f.isShipDeli,
  pss: f.pss || null,
  paymentWeightTerm: f.paymentWeightTerm || null,
  franchise: toNum(f.franchise),
  paymentTerms: f.paymentTerms || null,
  conditionsCode: f.conditionsCode || null,
  note: f.note || null,
  freight: toNum(f.freight),
  logisticsCost: toNum(f.logisticsCost),
  insuranceCost: toNum(f.insuranceCost),
  weightingCost: toNum(f.weightingCost),
  financeCost: toNum(f.financeCost),
  bankCost: toNum(f.bankCost),
  description: f.description || null,
  isIncludedRoyal: f.isIncludedRoyal,
  royalFee: toNum(f.royalFee),
  brokerCode: f.brokerCode || null,
  brokerRef: f.brokerRef || null,
  commission: toNum(f.commission),
  traderCode: f.traderCode || null,
  sentContract: f.sentContract,
  sentPaper: f.sentPaper,
  receivedContract: f.receivedContract,
  receivedPaper: f.receivedPaper,
})

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface ContractFormDialogProps {
  open: boolean
  onClose: () => void
  mode: 'new' | 'copy' | 'edit'
  contractId?: number
  contractNo?: string
  initialDraft?: SaleContractPayload | null
  controllerRef?: MutableRefObject<SaleContractFormController | null>
}

export interface SaleContractFormController {
  setField: (field: string, value: unknown) => string
  save: () => Promise<string>
  cancel: () => string
  getState: () => string
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save contract. Please try again.'
  if (!('data' in error)) return 'Failed to save contract. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save contract. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save contract. Please try again.'
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ContractFormDialog({ open, onClose, mode, contractId, contractNo, initialDraft, controllerRef }: ContractFormDialogProps) {
  const [form, setForm] = useState<SaleContractFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  /* ----- RTK Query hooks ----- */
  const { data: suggestedNo } = useSuggestContractNoQuery(undefined, { skip: mode !== 'new' || !open })
  const { data: editData } = useGetSaleContractQuery(contractNo!, { skip: mode !== 'edit' || !contractNo || !open })
  const [copyDraft, { data: copyData, isLoading: isCopying }] = useCopyDraftMutation()
  const [createContract, { isLoading: isCreating }] = useCreateSaleContractMutation()
  const [updateContract, { isLoading: isUpdating }] = useUpdateSaleContractMutation()

  /* Lookup selects (loaded once) */
  const { data: contractTypes } = useGetLookupContractTypesQuery(undefined, { skip: !open })
  const { data: fixationCalls } = useGetLookupFixationCallsQuery(undefined, { skip: !open })
  const { data: priceUnits } = useGetLookupPriceUnitsQuery(undefined, { skip: !open })
  const { data: futuresMonths } = useGetLookupFuturesMonthsQuery(undefined, { skip: !open })
  const { data: deliveryTerms } = useGetLookupDeliveryTermsQuery(undefined, { skip: !open })
  const { data: paymentWeightTerms } = useGetLookupPaymentWeightTermsQuery(undefined, { skip: !open })
  const { data: conditions } = useGetLookupConditionsQuery(undefined, { skip: !open })
  const { data: certifications } = useGetLookupCertificationsQuery(undefined, { skip: !open })

  /* Lazy autocomplete queries */
  const [fetchClients, { data: clientOpts, isFetching: clientsLoading }] = useLazyGetLookupClientsQuery()
  const [fetchConsignees, { data: consigneeOpts, isFetching: consigneesLoading }] = useLazyGetLookupConsigneesQuery()
  const [fetchPackages, { data: packageOpts, isFetching: packagesLoading }] = useLazyGetLookupPackagesQuery()
  const [fetchQualities, { data: qualityOpts, isFetching: qualitiesLoading }] = useLazyGetLookupQualitiesQuery()
  const [fetchBrokers, { data: brokerOpts, isFetching: brokersLoading }] = useLazyGetLookupBrokersQuery()
  const [fetchTraders, { data: traderOpts, isFetching: tradersLoading }] = useLazyGetLookupTradersQuery()

  /* ---- Cached packaging meta for bags derivation ---- */
  const packagingMeta = useRef<Record<string, number>>({})

  /* ---- Initialize form on open ---- */
  useEffect(() => {
    if (!open) return
    if (mode === 'new') {
      if (initialDraft) {
        setForm(contractToForm(initialDraft))
        setValidationError(null)
        return
      }
      const f = emptyForm()
      if (suggestedNo) f.contractNo = suggestedNo.contractNo
      setForm(f)
    } else if (mode === 'edit' && editData) {
      setForm(contractToForm(editData))
    } else if (mode === 'copy' && contractId) {
      copyDraft(contractId)
    }
    setValidationError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, editData, suggestedNo, initialDraft])

  useEffect(() => {
    if (mode === 'copy' && copyData?.draft) {
      setForm(contractToForm(copyData.draft))
    }
  }, [mode, copyData])

  /* Pre-load autocomplete options on dialog open */
  useEffect(() => {
    if (!open) return
    fetchClients()
    fetchConsignees()
    fetchPackages()
    fetchQualities()
    fetchBrokers()
    fetchTraders()
  }, [open, fetchClients, fetchConsignees, fetchPackages, fetchQualities, fetchBrokers, fetchTraders])

  /* ---- Helpers ---- */
  const set = useCallback(
    <K extends keyof SaleContractFormModel>(key: K, value: SaleContractFormModel[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  )

  const setNum = useCallback(
    (key: keyof SaleContractFormModel, raw: string) =>
      setForm((prev) => ({ ...prev, [key]: raw === '' ? '' : Number(raw) })),
    [],
  )

  const debouncedSearch = useCallback(
    (fetcher: (q: string) => void, q: string) => {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => fetcher(q), 300)
    },
    [],
  )

  const sdCur = form.isShipDeli ?? 1
  const showShipment = sdCur === 0 || sdCur === 1
  const showDelivery = sdCur === 0 || sdCur === 2

  /* Bags derivation */
  useEffect(() => {
    const qty = toNum(form.quantity)
    const weight = packagingMeta.current[form.packagingCode]
    if (qty != null && weight != null && weight > 0) {
      setForm((prev) => ({ ...prev, bags: Math.round(qty / weight) }))
    }
  }, [form.quantity, form.packagingCode])

  /* ---- Lookup option lists ---- */
  const clientItems = useMemo(() => clientOpts?.items ?? [], [clientOpts])
  const consigneeItems = useMemo(() => consigneeOpts?.items ?? [], [consigneeOpts])
  const packageItems = useMemo(() => packageOpts?.items ?? [], [packageOpts])
  const qualityItems = useMemo(() => qualityOpts?.items ?? [], [qualityOpts])
  const brokerItems = useMemo(() => brokerOpts?.items ?? [], [brokerOpts])
  const traderItems = useMemo(() => traderOpts?.items ?? [], [traderOpts])

  const validateLikeAccess = (): string | null => {
    if (!form.contractDate) return 'Please choose Contract date!'
    if (!form.contractNo.trim()) return 'Please type Contract no!'
    if (!form.clientCode.trim()) return 'Please choose Client!'
    if (form.quantity === '') return 'Please type Quantity!'
    if (!form.packagingCode.trim()) return 'Please choose Package!'
    if (form.bags === '') return 'Please type No of bags!'
    if (!form.qualityCode.trim()) return 'Please choose Quality!'
    if (!form.certificationCode.trim()) return 'Please choose Certification!'
    if (!form.contractType.trim()) return 'Please choose Contract Type!'

    const isOutright = form.contractType.trim().toLowerCase() === 'outright'
    const isDifferential = form.contractType.trim().toLowerCase() === 'differential'
    if (isOutright && form.price === '') return 'Please type Price!'
    if (isDifferential && form.ptbfDiff === '') return 'Please type PTBF diffs!'
    if (form.ptbfDiff !== '' && form.price !== '') return 'Choose only Price or PTBF diffs!'
    if (!form.priceUnit.trim()) return 'Please choose Price unit!'
    if (!form.deliveryTerm.trim()) return 'Please choose Delivery Term!'

    if (showShipment) {
      if (!form.shipmentDateFrom) return 'Please choose Shipment From!'
      if (!form.shipmentDateTo) return 'Please choose Shipment To!'
    }
    if (showDelivery) {
      if (!form.deliveryDateFrom) return 'Please choose Delivery From!'
      if (!form.deliveryDateTo) return 'Please choose Delivery To!'
    }
    if ((showShipment && showDelivery) || (!showShipment && !showDelivery)) {
      return 'Please tick to display either Shipment Month or Delivery Month!'
    }

    const term = form.deliveryTerm.trim().toUpperCase()
    if ((term.startsWith('CIF') || term.startsWith('CFR') || term.startsWith('CNF')) && form.freight === '') {
      return 'Please type Freight!'
    }
    if (!form.traderCode.trim()) return 'Please choose Trader In Charge!'
    if (form.vatApplicable && form.vatRate === '') return 'Please input VAT Rate!'
    if (!form.vatApplicable && form.vatRate !== '') return 'Please do not input VAT Rate!'
    return null
  }

  /* ---- Save ---- */
  const handleSave = async () => {
    const accessError = validateLikeAccess()
    if (accessError) {
      setValidationError(accessError)
      return
    }
    setValidationError(null)
    const body = formToPayload(form)
    try {
      if (mode === 'edit' && contractId) {
        await updateContract({ id: contractId, body }).unwrap()
      } else {
        await createContract(body).unwrap()
      }
      onClose()
    } catch (e: unknown) {
      setValidationError(extractApiErrorMessage(e))
    }
  }

  const handleCancel = () => {
    clearTimeout(debounceRef.current)
    setForm(emptyForm())
    setValidationError(null)
    onClose()
  }

  const isBusy = isCreating || isUpdating || isCopying

  const dialogTitle = 'Sale Contract - Sale'

  useEffect(() => {
    if (!controllerRef) return
    controllerRef.current = {
      setField: (field, value) => {
        if (!(field in form)) {
          return `Field "${field}" không tồn tại trong Sale Contract form.`
        }
        setForm((prev) => ({ ...prev, [field]: value } as SaleContractFormModel))
        return `Đã cập nhật ${field}.`
      },
      save: async () => {
        await handleSave()
        return 'Đã chạy thao tác lưu Sale Contract.'
      },
      cancel: () => {
        handleCancel()
        return 'Đã đóng form Sale Contract.'
      },
      getState: () =>
        `Sale form open=${open}, mode=${mode}, contractNo=${form.contractNo || '(empty)'}, clientCode=${form.clientCode || '(empty)'}`,
    }
    return () => {
      controllerRef.current = null
    }
  }, [controllerRef, form, handleSave, handleCancel, mode, open])

  /* ---- Autocomplete helper ---- */
  const renderAutocomplete = (
    label: string,
    value: string,
    options: LookupItem[],
    loading: boolean,
    tabIndex: number,
    onInputChange: (q: string) => void,
    onChange: (item: LookupItem | null) => void,
  ) => (
    <Autocomplete
      size="small"
      options={options}
      loading={loading}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      inputValue={value}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') {
          onInputChange(v)
        }
      }}
      onChange={(_, v) => onChange(v)}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{
            ...params.inputProps,
            tabIndex,
          }}
        />
      )}
    />
  )

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography component="span" sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}>
          {dialogTitle}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={isBusy}
            sx={{
              py: 0.25,
              px: 1,
              minHeight: 28,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              textTransform: 'none',
            }}
          >
            {isBusy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            sx={{
              py: 0.25,
              px: 1,
              minHeight: 28,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': {
            minHeight: 34,
            fontSize: '0.82rem',
          },
          '& .MuiInputBase-input': {
            py: 0.625,
            fontSize: '0.82rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.82rem',
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.82rem',
          },
          '& .MuiCheckbox-root': {
            p: 0.5,
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.05rem',
          },
          '& .MuiTypography-caption': {
            fontSize: '0.7rem',
          },
          '& .MuiTypography-body2': {
            fontSize: '0.82rem',
          },
        }}
      >
        {validationError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {validationError}
          </Typography>
        )}

        {/* Two-column grid — Row/Column pairs per DocConvert §8.5.1a. */}
        <Box sx={{ maxWidth: 980, mx: 'auto', pb: 1.5 }}>
          <Grid container spacing={0.75}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                ID
              </Typography>
              <Typography variant="body2" sx={{ minHeight: 34, lineHeight: '34px', color: 'text.disabled' }}>
                {mode === 'edit' && contractId != null ? contractId : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contract Date"
                type="date"
                size="small"
                fullWidth
                value={form.contractDate}
                onChange={(e) => set('contractDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Contract No"
                size="small"
                fullWidth
                value={form.contractNo}
                onChange={(e) => set('contractNo', e.target.value)}
                InputProps={{ readOnly: mode === 'edit' }}
                inputProps={{ tabIndex: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Buyer's Ref"
                size="small"
                fullWidth
                value={form.buyerRef}
                onChange={(e) => set('buyerRef', e.target.value)}
                inputProps={{ tabIndex: 3 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Client Name',
                form.clientName || form.clientCode,
                clientItems,
                clientsLoading,
                4,
                (q) => {
                  set('clientName', q)
                  debouncedSearch((s) => fetchClients(s), q)
                },
                (item) => {
                  set('clientCode', item?.value ?? '')
                  set('clientName', item?.label ?? '')
                },
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Client Code" size="small" fullWidth value={form.clientCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 5 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Consignee',
                form.consigneeName || form.consigneeCode,
                consigneeItems,
                consigneesLoading,
                6,
                (q) => {
                  set('consigneeName', q)
                  debouncedSearch((s) => fetchConsignees(s), q)
                },
                (item) => {
                  set('consigneeCode', item?.value ?? '')
                  set('consigneeName', item?.label ?? '')
                },
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity (Kg)"
                size="small"
                fullWidth
                type="number"
                value={form.quantity}
                onChange={(e) => setNum('quantity', e.target.value)}
                inputProps={{ tabIndex: 7 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Packaging',
                form.packagingName || form.packagingCode,
                packageItems,
                packagesLoading,
                8,
                (q) => {
                  set('packagingName', q)
                  debouncedSearch((s) => fetchPackages(s), q)
                },
                (item) => {
                  set('packagingCode', item?.value ?? '')
                  set('packagingName', item?.label ?? '')
                  const nw = item?.meta?.nw_per_bag_kg
                  if (nw != null && item?.value) {
                    packagingMeta.current[item.value] = Number(nw)
                  }
                },
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Bags" size="small" fullWidth type="number" value={form.bags} onChange={(e) => setNum('bags', e.target.value)} inputProps={{ tabIndex: 9 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Quality',
                form.qualityCode,
                qualityItems,
                qualitiesLoading,
                10,
                (q) => {
                  set('qualityCode', q)
                  debouncedSearch((s) => fetchQualities(s), q)
                },
                (item) => set('qualityCode', item?.value ?? ''),
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Certification</InputLabel>
                <Select
                  label="Certification"
                  value={form.certificationCode}
                  onChange={(e) => set('certificationCode', String(e.target.value))}
                  inputProps={{ tabIndex: 11 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(certifications?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Contract Type</InputLabel>
                <Select
                  label="Contract Type"
                  value={form.contractType}
                  onChange={(e) => set('contractType', String(e.target.value))}
                  inputProps={{ tabIndex: 12 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(contractTypes?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Fixation Call</InputLabel>
                <Select
                  label="Fixation Call"
                  value={form.fixationCall}
                  onChange={(e) => set('fixationCall', String(e.target.value))}
                  inputProps={{ tabIndex: 13 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(fixationCalls?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Price" size="small" fullWidth type="number" value={form.price} onChange={(e) => setNum('price', e.target.value)} inputProps={{ tabIndex: 14 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Price Unit</InputLabel>
                <Select
                  label="Price Unit"
                  value={form.priceUnit}
                  onChange={(e) => set('priceUnit', String(e.target.value))}
                  inputProps={{ tabIndex: 15 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(priceUnits?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="PTBF Diffs" size="small" fullWidth type="number" value={form.ptbfDiff} onChange={(e) => setNum('ptbfDiff', e.target.value)} inputProps={{ tabIndex: 16 }} />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ ml: 0, mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(form.vatApplicable)}
                    tabIndex={17}
                    onChange={(e) => {
                      const checked = e.target.checked
                      set('vatApplicable', checked)
                      if (!checked) set('vatRate', '')
                    }}
                  />
                }
                label="Applicable VAT?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="VAT Rate" size="small" fullWidth type="number" value={form.vatRate} onChange={(e) => setNum('vatRate', e.target.value)} inputProps={{ tabIndex: 18 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Futures Month</InputLabel>
                <Select
                  label="Futures Month"
                  value={form.futuresMonth}
                  onChange={(e) => set('futuresMonth', String(e.target.value))}
                  inputProps={{ tabIndex: 19 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(futuresMonths?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="FX" size="small" fullWidth type="number" value={form.fxRate} onChange={(e) => setNum('fxRate', e.target.value)} inputProps={{ tabIndex: 20 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Delivery Term</InputLabel>
                <Select
                  label="Delivery Term"
                  value={form.deliveryTerm}
                  onChange={(e) => set('deliveryTerm', String(e.target.value))}
                  inputProps={{ tabIndex: 21 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(deliveryTerms?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Warehouse" size="small" fullWidth value={form.warehouse} onChange={(e) => set('warehouse', e.target.value)} inputProps={{ tabIndex: 22 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={showShipment}
                    tabIndex={23}
                    onChange={(e) => {
                      const checked = e.target.checked
                      const cur = form.isShipDeli ?? 0
                      const deliOn = cur === 0 || cur === 2
                      if (checked) {
                        set('isShipDeli', deliOn ? 0 : 1)
                      } else {
                        set('isShipDeli', deliOn ? 2 : 3)
                      }
                    }}
                  />
                }
                label="Show Shipment Mo.?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={showDelivery}
                    tabIndex={24}
                    onChange={(e) => {
                      const checked = e.target.checked
                      const cur = form.isShipDeli ?? 0
                      const shipOn = cur === 0 || cur === 1
                      if (checked) {
                        set('isShipDeli', shipOn ? 0 : 2)
                      } else {
                        set('isShipDeli', shipOn ? 1 : 3)
                      }
                    }}
                  />
                }
                label="Show Delivery Mo.?"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Shipment From"
                type="date"
                size="small"
                fullWidth
                disabled={!showShipment}
                value={form.shipmentDateFrom}
                onChange={(e) => set('shipmentDateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: showShipment ? 25 : -1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Delivery From"
                type="date"
                size="small"
                fullWidth
                disabled={!showDelivery}
                value={form.deliveryDateFrom}
                onChange={(e) => set('deliveryDateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: showDelivery ? 26 : -1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Shipment To"
                type="date"
                size="small"
                fullWidth
                disabled={!showShipment}
                value={form.shipmentDateTo}
                onChange={(e) => set('shipmentDateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: showShipment ? 27 : -1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Delivery To"
                type="date"
                size="small"
                fullWidth
                disabled={!showDelivery}
                value={form.deliveryDateTo}
                onChange={(e) => set('deliveryDateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: showDelivery ? 28 : -1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ minHeight: { xs: 4, sm: 10 } }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="PSS Terms" size="small" fullWidth value={form.pss} onChange={(e) => set('pss', e.target.value)} inputProps={{ tabIndex: 29 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Payment weight terms</InputLabel>
                <Select
                  label="Payment weight terms"
                  value={form.paymentWeightTerm}
                  onChange={(e) => set('paymentWeightTerm', String(e.target.value))}
                  inputProps={{ tabIndex: 30 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(paymentWeightTerms?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Franchise" size="small" fullWidth type="number" value={form.franchise} onChange={(e) => setNum('franchise', e.target.value)} inputProps={{ tabIndex: 31 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment terms"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={form.paymentTerms}
                onChange={(e) => set('paymentTerms', e.target.value)}
                inputProps={{ tabIndex: 32 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Conditions</InputLabel>
                <Select
                  label="Conditions"
                  value={form.conditionsCode}
                  onChange={(e) => set('conditionsCode', String(e.target.value))}
                  inputProps={{ tabIndex: 33 }}
                >
                  <MenuItem value="">(None)</MenuItem>
                  {(conditions?.items ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ alignSelf: 'stretch' }}>
              <Box sx={{ minHeight: { sm: 56 } }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Note"
                size="small"
                fullWidth
                multiline
                minRows={6}
                value={form.note}
                onChange={(e) => set('note', e.target.value)}
                inputProps={{ tabIndex: 34 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Freight" size="small" fullWidth type="number" value={form.freight} onChange={(e) => setNum('freight', e.target.value)} inputProps={{ tabIndex: 35 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Logistics cost" size="small" fullWidth type="number" value={form.logisticsCost} onChange={(e) => setNum('logisticsCost', e.target.value)} inputProps={{ tabIndex: 36 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Finance cost" size="small" fullWidth type="number" value={form.financeCost} onChange={(e) => setNum('financeCost', e.target.value)} inputProps={{ tabIndex: 37 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Insurance cost" size="small" fullWidth type="number" value={form.insuranceCost} onChange={(e) => setNum('insuranceCost', e.target.value)} inputProps={{ tabIndex: 38 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Bank charges" size="small" fullWidth type="number" value={form.bankCost} onChange={(e) => setNum('bankCost', e.target.value)} inputProps={{ tabIndex: 39 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Weighing cost" size="small" fullWidth type="number" value={form.weightingCost} onChange={(e) => setNum('weightingCost', e.target.value)} inputProps={{ tabIndex: 40 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                inputProps={{ tabIndex: 41 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                <FormControlLabel
                  sx={{ ml: 0, mr: 0 }}
                  control={
                    <Checkbox size="small" checked={form.isIncludedRoyal} tabIndex={42} onChange={(e) => set('isIncludedRoyal', e.target.checked)} />
                  }
                  label="Royal Fee included ?"
                />
                <TextField
                  label="Royal Fee"
                  size="small"
                  type="number"
                  sx={{ flex: '1 1 120px', minWidth: 100 }}
                  value={form.royalFee}
                  onChange={(e) => setNum('royalFee', e.target.value)}
                  inputProps={{ tabIndex: 43 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Broker Code',
                form.brokerCode,
                brokerItems,
                brokersLoading,
                44,
                (q) => {
                  set('brokerCode', q)
                  debouncedSearch((s) => fetchBrokers(s), q)
                },
                (item) => {
                  set('brokerCode', item?.value ?? '')
                  const sn = item?.meta?.short_name as string | undefined
                  set('brokerFirm', (typeof sn === 'string' ? sn : '') || item?.label?.split('—')[1]?.trim() || '')
                },
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Broker Name" size="small" fullWidth value={form.brokerFirm} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 45 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Broker's Ref" size="small" fullWidth value={form.brokerRef} onChange={(e) => set('brokerRef', e.target.value)} inputProps={{ tabIndex: 46 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Commission (USD/MT)"
                size="small"
                fullWidth
                type="number"
                value={form.commission}
                onChange={(e) => setNum('commission', e.target.value)}
                inputProps={{ tabIndex: 47 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Trader In Charge',
                form.traderName,
                traderItems,
                tradersLoading,
                48,
                (q) => {
                  set('traderName', q)
                  debouncedSearch((s) => fetchTraders(s), q)
                },
                (item) => {
                  set('traderCode', item?.value ?? '')
                  set('traderName', item?.label ?? '')
                },
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="PIC" size="small" fullWidth value={form.traderCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 49 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Consignee Code" size="small" fullWidth value={form.consigneeCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 50 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Packaging Code" size="small" fullWidth value={form.packagingCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 51 }} />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.sentContract} onChange={(e) => set('sentContract', e.target.checked)} tabIndex={52} />}
                  label="Sent contract"
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.receivedContract} onChange={(e) => set('receivedContract', e.target.checked)} tabIndex={53} />}
                  label="Received contract"
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.sentPaper} onChange={(e) => set('sentPaper', e.target.checked)} tabIndex={54} />}
                  label="Sent paper"
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.receivedPaper} onChange={(e) => set('receivedPaper', e.target.checked)} tabIndex={55} />}
                  label="Received paper"
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
